import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Heart, HandHeart, HandCoins, MapPin, ArrowRight, Users, BellRing,
  Smartphone, Play, Check, Star, Menu, X, ShieldCheck, Megaphone, ChevronDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import TripGlobe from "../components/TripGlobe";

const NAVY = "#052A3F";
const GOLD = "#C5A059";

const GLOBE_MARKERS = [
  { name: "Moldova", lat: 47.0105, lng: 28.8638, current: true },
  { name: "Kenya", lat: -1.2921, lng: 36.8219 },
  { name: "Philippines", lat: 14.5995, lng: 120.9842 },
  { name: "India", lat: 28.6139, lng: 77.209 },
];
const GLOBE_ORIGIN = { country: "United States", lat: 37.7595, lng: -122.4367 };

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const MOCKUPS = {
  feed: "https://static.prod-images.emergentagent.com/jobs/5ed0f3d3-1084-40b5-ba13-bb252801f3ad/images/31026f63fc975824b64778eb48a8876e60a9da1775c3fcd52ee0c0552e127bc7.png",
  budget: "https://static.prod-images.emergentagent.com/jobs/5ed0f3d3-1084-40b5-ba13-bb252801f3ad/images/69b940281071647b51f84430a2df4fbf664bf37feed77e5f305cd53dd6fff5c0.png",
  prayer: "https://static.prod-images.emergentagent.com/jobs/5ed0f3d3-1084-40b5-ba13-bb252801f3ad/images/e8ca187d4de3135c866f8b57462c78310b84016b7941a4fb307dd1252a473227.png",
};
const HERO_IMG = "https://images.unsplash.com/photo-1655720359248-eeace8c709c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

/* ---------- Navigation ---------- */
function Nav({ navigate }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { label: "Features", href: "#features" },
    { label: "How it works", href: "#product" },
    { label: "Pricing", href: "#pricing" },
    { label: "Stories", href: "#stories" },
  ];
  return (
    <header
      data-testid="landing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled ? "bg-white/75 backdrop-blur-xl border-b border-black/5 shadow-[0_2px_20px_rgba(5,42,63,0.06)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-6 h-16 flex items-center justify-between">
        <BrandLogo size="md" />
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.label} href={l.href} className="text-sm font-medium text-[#4A6170] hover:text-[#052A3F] transition-colors">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <Button data-testid="nav-login-btn" variant="ghost" className="rounded-full text-[#052A3F] hover:bg-[#052A3F]/5" onClick={() => navigate("/auth")}>
            Sign in
          </Button>
          <Button data-testid="nav-start-btn" className="rounded-full text-white h-10 px-5 hover:brightness-95 transition-[filter]" style={{ backgroundColor: GOLD }} onClick={() => navigate("/auth?mode=org")}>
            Start free
          </Button>
        </div>
        <button data-testid="nav-menu-toggle" className="md:hidden p-2 text-[#052A3F]" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-black/5 px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="py-2.5 text-[#052A3F] font-medium">
              {l.label}
            </a>
          ))}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-full border-[#052A3F]/20 text-[#052A3F]" onClick={() => navigate("/auth")}>Sign in</Button>
            <Button className="flex-1 rounded-full text-white" style={{ backgroundColor: GOLD }} onClick={() => navigate("/auth?mode=org")}>Start free</Button>
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------- Hero ---------- */
function Hero({ navigate }) {
  return (
    <section className="relative overflow-hidden pt-28 sm:pt-36 pb-20">
      <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(197,160,89,0.35), transparent 70%)" }} />
      <div className="absolute -bottom-52 -left-52 h-[520px] w-[520px] rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, rgba(5,42,63,0.25), transparent 70%)" }} />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <div>
          <motion.span variants={fadeUp} initial="hidden" animate="show" className="inline-flex items-center gap-2 rounded-full bg-[#052A3F]/5 border border-[#052A3F]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#4A6170]">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: GOLD }} /> The mission-trip companion
          </motion.span>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1} className="font-heading font-bold text-[#052A3F] mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Bring your partners <span style={{ color: GOLD }}>on the journey.</span>
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-6 text-lg text-[#4A6170] max-w-md leading-relaxed">
            Run your mission trip and keep every supporter close — share dispatches, track your budget with total transparency, and rally partners in prayer. All from one beautiful app.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap gap-3">
            <Button data-testid="hero-cta-start" className="rounded-full text-white h-13 px-8 text-base shadow-lg shadow-[#C5A059]/30 hover:brightness-95 hover:-translate-y-0.5 transition-[filter,transform]" style={{ backgroundColor: GOLD, height: 52 }} onClick={() => navigate("/auth?mode=org")}>
              Start your trip free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button data-testid="hero-cta-partner" variant="outline" className="rounded-full h-13 px-8 text-base border-[#052A3F]/20 text-[#052A3F] hover:bg-[#052A3F]/5 transition-colors" style={{ height: 52 }} onClick={() => navigate("/auth?mode=join")}>
              I'm a partner
            </Button>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#4A6170]">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Check className="h-4 w-4" style={{ color: GOLD }} /> Free to start</span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Check className="h-4 w-4" style={{ color: GOLD }} /> No card needed</span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Check className="h-4 w-4" style={{ color: GOLD }} /> 5-min setup</span>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="relative">
          <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(5,42,63,0.22)] rotate-1">
            <img src={HERO_IMG} alt="Mission community together" className="w-full aspect-[4/5] object-cover" />
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }} className="absolute -bottom-6 -left-4 bg-white rounded-2xl p-4 shadow-[0_12px_40px_rgba(5,42,63,0.16)] w-48">
            <div className="text-xs text-[#4A6170]">Raised toward goal</div>
            <div className="font-heading font-bold text-2xl text-[#052A3F]">$25,300</div>
            <div className="mt-2 h-2 rounded-full bg-[#F3EFE7] overflow-hidden">
              <motion.div className="h-full rounded-full" style={{ backgroundColor: GOLD }} initial={{ width: 0 }} animate={{ width: "56%" }} transition={{ delay: 0.8, duration: 1 }} />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.6 }} className="absolute -top-4 -right-3 bg-white rounded-2xl px-4 py-3 shadow-[0_12px_40px_rgba(5,42,63,0.16)] flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ backgroundColor: "#F2E8D3" }}>
              <HandHeart className="h-4.5 w-4.5" style={{ color: GOLD }} />
            </div>
            <div>
              <div className="font-heading font-bold text-sm text-[#052A3F] leading-none">142 praying</div>
              <div className="text-[11px] text-[#4A6170] mt-0.5">for safe travels</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- Trust marquee ---------- */
function TrustRibbon() {
  const items = ["Churches", "Missions Agencies", "Youth Groups", "Nonprofits", "Campus Ministries", "Relief Teams", "Bible Colleges", "Sending Orgs"];
  return (
    <div className="border-y border-[#052A3F]/8 bg-[#F3EFE7]/60 py-5">
      <p className="text-center text-xs uppercase tracking-widest text-[#4A6170] mb-3">Built for teams that go</p>
      <Marquee gradient gradientColor="#FDFBF7" gradientWidth={80} speed={35}>
        {items.concat(items).map((it, i) => (
          <span key={i} className="mx-8 font-heading text-xl text-[#052A3F]/40 font-semibold">{it}</span>
        ))}
      </Marquee>
    </div>
  );
}

/* ---------- Bento features ---------- */
function BentoCard({ children, className = "", i = 0, testid }) {
  return (
    <motion.div
      variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
      data-testid={testid}
      className={`rounded-3xl border border-[#E5E0D8] bg-white p-7 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(5,42,63,0.1)] transition-[transform,box-shadow] duration-300 ${className}`}
    >
      {children}
    </motion.div>
  );
}
function FeatureIcon({ Icon }) {
  return (
    <div className="h-12 w-12 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "#F2E8D3" }}>
      <Icon className="h-5.5 w-5.5" style={{ color: GOLD }} strokeWidth={2} />
    </div>
  );
}
function Features() {
  return (
    <section id="features" className="mx-auto max-w-6xl px-5 sm:px-6 py-24 scroll-mt-20">
      <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl mb-14">
        <span className="text-sm font-semibold uppercase tracking-widest" style={{ color: GOLD }}>Everything in one place</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#052A3F] mt-3 leading-tight">One home for your trip — and everyone cheering you on</h2>
      </motion.div>

      <div className="grid md:grid-cols-12 gap-5">
        <BentoCard i={0} testid="feature-feed" className="md:col-span-7">
          <FeatureIcon Icon={Heart} />
          <h3 className="font-heading font-bold text-2xl text-[#052A3F]">Dispatch Feed</h3>
          <p className="text-[#4A6170] mt-2 leading-relaxed max-w-md">Post photos, short videos and country stories from the field. Partners react with ❤️ 🙏 ✨ and comment in real time — so nobody feels far away.</p>
          <div className="mt-6 flex gap-2 flex-wrap">
            {["Photos & video", "Reactions", "Comments", "Featured image"].map((t) => (
              <span key={t} className="text-xs font-medium rounded-full bg-[#F3EFE7] text-[#4A6170] px-3 py-1.5">{t}</span>
            ))}
          </div>
        </BentoCard>

        <BentoCard i={1} testid="feature-globe" className="md:col-span-5 flex flex-col items-center text-center overflow-hidden">
          <div className="w-full flex-1 flex items-center justify-center min-h-[240px] py-2">
            <TripGlobe markers={GLOBE_MARKERS} origin={GLOBE_ORIGIN} size={300} />
          </div>
          <h3 className="font-heading font-bold text-2xl text-[#052A3F] mt-2">Trip Itinerary</h3>
          <p className="text-[#4A6170] mt-1.5 text-sm">A living map of your journey — countries, flights, lodging and a spinning globe that shows exactly where the team is right now.</p>
        </BentoCard>

        <BentoCard i={2} testid="feature-budget" className="md:col-span-4">
          <FeatureIcon Icon={HandCoins} />
          <h3 className="font-heading font-bold text-xl text-[#052A3F]">Live Budget</h3>
          <p className="text-[#4A6170] mt-2 text-sm leading-relaxed">Transparent giving. Watch the goal fill up with every gift, and log expenses by country and category.</p>
        </BentoCard>

        <BentoCard i={3} testid="feature-prayer" className="md:col-span-4">
          <FeatureIcon Icon={HandHeart} />
          <h3 className="font-heading font-bold text-xl text-[#052A3F]">Prayer Wall</h3>
          <p className="text-[#4A6170] mt-2 text-sm leading-relaxed">Post requests, flag the urgent ones, and let supporters pray alongside you — publicly or privately.</p>
        </BentoCard>

        <BentoCard i={4} testid="feature-team" className="md:col-span-4">
          <FeatureIcon Icon={Users} />
          <h3 className="font-heading font-bold text-xl text-[#052A3F]">Your Team</h3>
          <p className="text-[#4A6170] mt-2 text-sm leading-relaxed">Add teammates with roles & permissions, a "Meet the Team" roster, and a private team-only channel.</p>
        </BentoCard>

        <BentoCard i={5} testid="feature-push" className="md:col-span-5 bg-[#052A3F] border-[#052A3F]">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center mb-4 bg-white/10">
            <BellRing className="h-5.5 w-5.5" style={{ color: GOLD }} />
          </div>
          <h3 className="font-heading font-bold text-xl text-white">Push Notifications</h3>
          <p className="text-white/70 mt-2 text-sm leading-relaxed">Send special announcements and instant alerts. Partners get a ping for new dispatches and urgent prayer — right on their phone.</p>
        </BentoCard>

        <BentoCard i={6} testid="feature-pwa" className="md:col-span-7 flex items-center gap-6">
          <div>
            <FeatureIcon Icon={Smartphone} />
            <h3 className="font-heading font-bold text-xl text-[#052A3F]">Installable app (PWA)</h3>
            <p className="text-[#4A6170] mt-2 text-sm leading-relaxed max-w-md">Add to home screen on iPhone or Android — works offline, feels native, and updates instantly. No app store required.</p>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-[#052A3F]/15">
            <ShieldCheck className="h-16 w-16" strokeWidth={1.2} />
            <Megaphone className="h-14 w-14" strokeWidth={1.2} />
          </div>
        </BentoCard>
      </div>
    </section>
  );
}

/* ---------- Product screenshots ---------- */
function Product() {
  const shots = [
    { src: MOCKUPS.feed, label: "Share dispatches", rotate: "-rotate-3" },
    { src: MOCKUPS.budget, label: "Transparent budget", rotate: "rotate-2", raised: true },
    { src: MOCKUPS.prayer, label: "Rally in prayer", rotate: "-rotate-2" },
  ];
  return (
    <section id="product" className="bg-[#F3EFE7]/60 py-24 scroll-mt-16">
      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm font-semibold uppercase tracking-widest" style={{ color: GOLD }}>See it in action</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#052A3F] mt-3 leading-tight">Designed for the field. Loved on the couch.</h2>
          <p className="text-[#4A6170] mt-4 leading-relaxed">Beautiful on every phone, so your team can post in seconds and your partners never miss a moment.</p>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-8 sm:gap-4 max-w-4xl mx-auto">
          {shots.map((s, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className={`flex flex-col items-center ${s.raised ? "sm:-mt-6" : "sm:mt-4"}`}>
              <div className={`rounded-[1.8rem] overflow-hidden border-[6px] border-[#052A3F] shadow-[0_24px_60px_rgba(5,42,63,0.2)] ${s.rotate} hover:rotate-0 transition-transform duration-500`}>
                <img src={s.src} alt={s.label} className="w-full max-w-[220px]" />
              </div>
              <span className="mt-6 font-heading font-semibold text-[#052A3F]">{s.label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Demo video ---------- */
function DemoVideo() {
  return (
    <section className="mx-auto max-w-5xl px-5 sm:px-6 py-24">
      <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-xl mx-auto mb-10">
        <span className="text-sm font-semibold uppercase tracking-widest" style={{ color: GOLD }}>Watch the tour</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#052A3F] mt-3">Two minutes to fall in love</h2>
      </motion.div>
      <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative rounded-3xl overflow-hidden group cursor-pointer shadow-[0_30px_80px_rgba(5,42,63,0.22)]" data-testid="demo-video">
        <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Product demo" className="w-full aspect-video object-cover" />
        <div className="absolute inset-0 bg-[#052A3F]/45 group-hover:bg-[#052A3F]/35 transition-colors flex items-center justify-center">
          <div className="h-20 w-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
            <Play className="h-8 w-8 ml-1" style={{ color: NAVY, fill: NAVY }} />
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ---------- Pricing ---------- */
const TIERS = [
  {
    name: "Starter", price: "$0", cadence: "forever", testid: "plan-free",
    tagline: "Perfect for a first trip.",
    features: ["1 active trip", "Up to 25 partners", "Dispatch feed & prayer wall", "Live budget & itinerary", "Interactive trip map"],
    cta: "Start free", highlight: false,
  },
  {
    name: "Growth", price: "$8", cadence: "/mo", annual: "or $80/yr", testid: "plan-growth",
    tagline: "For a growing supporter base.",
    features: ["Everything in Starter", "Unlimited partners", "Push notifications", "Special announcements", "Video uploads", "Remove MissionCompanion branding"],
    cta: "Choose Growth", highlight: false,
  },
  {
    name: "Pro", price: "$15", cadence: "/mo", annual: "or $144/yr · 7-day free trial", testid: "plan-pro",
    tagline: "For teams that go together.",
    features: ["Everything in Growth", "Team members & roles", "Private team channel", "Custom logo & branding", "Priority support"],
    cta: "Start 7-day trial", highlight: true,
  },
];
function Pricing({ navigate }) {
  return (
    <section id="pricing" className="mx-auto max-w-6xl px-5 sm:px-6 py-24 scroll-mt-16">
      <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-14">
        <span className="text-sm font-semibold uppercase tracking-widest" style={{ color: GOLD }}>Simple pricing</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#052A3F] mt-3 leading-tight">Start free. Upgrade when you grow.</h2>
        <p className="text-[#4A6170] mt-4">No hidden fees. Cancel anytime. Your data is always yours.</p>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-6 items-start">
        {TIERS.map((t, i) => (
          <motion.div
            key={t.name} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
            data-testid={t.testid}
            className={`rounded-3xl p-8 flex flex-col ${
              t.highlight
                ? "bg-[#052A3F] text-white shadow-[0_30px_70px_rgba(5,42,63,0.3)] md:-translate-y-4 border-2"
                : "bg-white border border-[#E5E0D8]"
            }`}
            style={t.highlight ? { borderColor: GOLD } : {}}
          >
            {t.highlight && (
              <span className="self-start mb-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold text-[#052A3F]" style={{ backgroundColor: GOLD }}>
                <Star className="h-3.5 w-3.5 fill-current" /> Most popular
              </span>
            )}
            <h3 className={`font-heading font-bold text-2xl ${t.highlight ? "text-white" : "text-[#052A3F]"}`}>{t.name}</h3>
            <p className={`text-sm mt-1 ${t.highlight ? "text-white/70" : "text-[#4A6170]"}`}>{t.tagline}</p>
            <div className="mt-6 flex items-end gap-1">
              <span className={`font-heading font-bold text-5xl ${t.highlight ? "text-white" : "text-[#052A3F]"}`}>{t.price}</span>
              <span className={`mb-1.5 text-sm ${t.highlight ? "text-white/70" : "text-[#4A6170]"}`}>{t.cadence}</span>
            </div>
            <p className={`text-xs mt-1 h-4 ${t.highlight ? "text-[#E8D5A8]" : "text-[#4A6170]"}`}>{t.annual || ""}</p>
            <Button
              data-testid={`${t.testid}-cta`}
              className={`rounded-full mt-6 h-12 font-semibold transition-[filter,transform] hover:-translate-y-0.5 ${
                t.highlight ? "text-[#052A3F] hover:brightness-95" : "text-white hover:brightness-95"
              }`}
              style={{ backgroundColor: t.highlight ? GOLD : NAVY }}
              onClick={() => navigate("/auth?mode=org")}
            >
              {t.cta}
            </Button>
            <ul className="mt-7 space-y-3">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <Check className="h-4.5 w-4.5 mt-0.5 shrink-0" style={{ color: t.highlight ? GOLD : GOLD }} />
                  <span className={t.highlight ? "text-white/85" : "text-[#4A6170]"}>{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Testimonials ---------- */
const STORIES = [
  { name: "Sarah M.", role: "Trip Leader · Kenya", img: "https://images.unsplash.com/photo-1611695434369-a8f5d76ceb7b?crop=entropy&cs=srgb&fm=jpg&q=85&w=200", quote: "Our supporters finally felt like they were on the trip with us. The prayer wall lit up every single day." },
  { name: "David R.", role: "Missions Pastor", img: "https://images.unsplash.com/photo-1625241152315-4a698f74ceb7?crop=entropy&cs=srgb&fm=jpg&q=85&w=200", quote: "The transparent budget built so much trust with our givers. No more spreadsheets and awkward updates." },
  { name: "Grace L.", role: "Partner & Donor", img: "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&q=85&w=200", quote: "I got a notification the moment they landed. I've never felt this connected to a team I'm supporting." },
  { name: "Michael T.", role: "Campus Ministry", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=200", quote: "Setup took five minutes and our students were posting dispatches from the field that night." },
];
function Testimonials() {
  return (
    <section id="stories" className="bg-[#F3EFE7]/60 py-24 scroll-mt-16 overflow-hidden">
      <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-14 px-5">
        <span className="text-sm font-semibold uppercase tracking-widest" style={{ color: GOLD }}>Loved by teams & partners</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#052A3F] mt-3">Stories from the field</h2>
      </motion.div>
      <Marquee gradient gradientColor="#F6F1E9" gradientWidth={60} speed={30} pauseOnHover>
        {STORIES.concat(STORIES).map((s, i) => (
          <div key={i} className="mx-4 w-[340px] shrink-0 bg-white rounded-3xl border border-[#E5E0D8] p-7 shadow-sm" data-testid={i < STORIES.length ? `testimonial-${i}` : undefined}>
            <div className="flex gap-0.5 mb-4">{[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-current" style={{ color: GOLD }} />)}</div>
            <p className="text-[#052A3F] leading-relaxed">"{s.quote}"</p>
            <div className="flex items-center gap-3 mt-5">
              <img src={s.img} alt={s.name} className="h-11 w-11 rounded-full object-cover" />
              <div>
                <div className="font-semibold text-[#052A3F] text-sm">{s.name}</div>
                <div className="text-xs text-[#4A6170]">{s.role}</div>
              </div>
            </div>
          </div>
        ))}
      </Marquee>
    </section>
  );
}

/* ---------- FAQ ---------- */
const FAQS = [
  { q: "Is it really free to start?", a: "Yes. The Starter plan is free forever for one trip with up to 25 partners — no credit card required. Upgrade only when you need more." },
  { q: "Do my partners need to download anything?", a: "No app store needed. MissionCompanion is a progressive web app — partners open a link and can 'Add to Home Screen' on iPhone or Android for an app-like experience." },
  { q: "How does the transparent budget work?", a: "You set a goal, then log gifts and expenses. Partners see a live progress bar and a breakdown by country and category — building trust automatically." },
  { q: "Can I invite my whole team?", a: "Yes, on the Pro plan. Add teammates with custom roles and permissions, a public 'Meet the Team' roster, and a private team-only chat channel." },
  { q: "Can I cancel anytime?", a: "Absolutely. Cancel whenever you like and your paid features stay active until the end of your billing period. Your data always remains yours." },
];
function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="mx-auto max-w-3xl px-5 sm:px-6 py-24">
      <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-heading font-bold text-3xl sm:text-4xl text-[#052A3F] text-center mb-12">
        Questions, answered
      </motion.h2>
      <div className="space-y-3">
        {FAQS.map((f, i) => (
          <div key={i} data-testid={`faq-${i}`} className="rounded-2xl border border-[#E5E0D8] bg-white overflow-hidden">
            <button className="w-full flex items-center justify-between gap-4 p-5 text-left" onClick={() => setOpen(open === i ? -1 : i)}>
              <span className="font-heading font-semibold text-[#052A3F] text-lg">{f.q}</span>
              <ChevronDown className={`h-5 w-5 shrink-0 text-[#4A6170] transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
            </button>
            <div className={`grid transition-[grid-template-rows] duration-300 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
              <div className="overflow-hidden"><p className="px-5 pb-5 text-[#4A6170] leading-relaxed">{f.a}</p></div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Footer CTA ---------- */
function FooterCta({ navigate }) {
  return (
    <footer className="bg-[#052A3F] text-white pt-24 pb-12">
      <div className="mx-auto max-w-5xl px-5 sm:px-6 text-center">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          Your next trip deserves <br className="hidden sm:block" /><span style={{ color: GOLD }}>a better companion.</span>
        </motion.h2>
        <p className="text-white/70 mt-6 max-w-lg mx-auto text-lg">Set up your trip in five minutes and bring every partner along for the journey.</p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Button data-testid="footer-cta-start" className="rounded-full text-[#052A3F] h-13 px-8 text-base font-semibold hover:brightness-95 hover:-translate-y-0.5 transition-[filter,transform]" style={{ backgroundColor: GOLD, height: 52 }} onClick={() => navigate("/auth?mode=org")}>
            Start your trip free <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button data-testid="footer-cta-signin" variant="outline" className="rounded-full h-13 px-8 text-base border-white/25 text-white hover:bg-white/10 transition-colors" style={{ height: 52 }} onClick={() => navigate("/auth")}>
            Sign in
          </Button>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-5 sm:px-6 mt-20 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <img src="/icon-192.png" alt="Mission Companion" className="h-7 w-7 rounded-lg" />
          <span className="font-heading font-bold text-lg">Mission Companion</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-white/60">
          <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
          <a href="/terms" className="hover:text-white transition-colors">Terms</a>
          <button className="hover:text-white transition-colors" onClick={() => navigate("/auth")}>Sign in</button>
        </div>
        <span className="text-xs text-white/40">© {new Date().getFullYear()} Mission Companion</span>
      </div>
    </footer>
  );
}

export default function Marketing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#052A3F] overflow-x-hidden" style={{ scrollBehavior: "smooth" }}>
      <Nav navigate={navigate} />
      <Hero navigate={navigate} />
      <TrustRibbon />
      <Features />
      <Product />
      <DemoVideo />
      <Pricing navigate={navigate} />
      <Testimonials />
      <Faq />
      <FooterCta navigate={navigate} />
    </div>
  );
}
